import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, locationAPI, friendsAPI, tripsAPI, reportsAPI, alertsAPI, sosAPI, subscriptionAPI, emergencyMarkerAPI } from '@/lib/api';
import { useRealtimeUpdates } from '@/hooks/useRealtimeUpdates';
import { registerPlugin } from '@capacitor/core';
import { LocationService, isNative, platform, DeepLinkService, AppLifecycleService, BatteryService } from '@/lib/capacitor';
import BackgroundLocationService, { hasBackgroundLocationConsent } from '@/lib/backgroundLocation';
import { toast } from 'sonner';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Auth state
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Map state
  const [publicBoaters, setPublicBoaters] = useState([]);
  const [friendsLocations, setFriendsLocations] = useState([]);
  const [nearbyReports, setNearbyReports] = useState([]);
  const [myLocation, setMyLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([25.7617, -80.1918]); // Miami
  const [focusLocation, setFocusLocation] = useState(null); // one-shot: fly map here (e.g. "locate" a crew member)

  // Friends state
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);

  // Trip state
  const [activeTrip, setActiveTrip] = useState(null);
  const [tripHistory, setTripHistory] = useState([]);

  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  // Subscription state (Boat 360 Freemium)
  const [subscription, setSubscription] = useState({ tier: 'FREE', is_paid: false, features: {} });
  const [activeSOS, setActiveSOS] = useState(null);
  const [nearbyPublicSOS, setNearbyPublicSOS] = useState([]);
  const [friendsSOS, setFriendsSOS] = useState([]);

  // Real-time connection state
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  // Device battery state (Crew Safety tracking)
  const [batteryInfo, setBatteryInfo] = useState({ level: null, isCharging: null });
  const batteryInfoRef = useRef({ level: null, isCharging: null });
  useEffect(() => {
    batteryInfoRef.current = batteryInfo;
  }, [batteryInfo]);
  
  // Emergency markers (MOB)
  const [emergencyMarkers, setEmergencyMarkers] = useState([]);

  // Location tracking
  const locationWatchId = useRef(null);
  const lastLocationUpdate = useRef(null);
  const myLocationRef = useRef(null);
  
  // Keep myLocationRef in sync
  useEffect(() => {
    myLocationRef.current = myLocation;
  }, [myLocation]);

  // Real-time update handlers
  const handleFriendLocationUpdate = useCallback((data) => {
    setFriendsLocations(prev => {
      const index = prev.findIndex(f => f.friend_user_id === data.user_id);
      const updated = {
        friend_user_id: data.user_id,
        display_name: data.display_name,
        lat: data.lat,
        lon: data.lon,
        speed: data.speed,
        heading: data.heading,
        motion_state: data.motion_state,
        is_on_trip: data.is_on_trip,
        updated_at: data.updated_at,
      };
      if (index >= 0) {
        const newList = [...prev];
        newList[index] = updated;
        return newList;
      }
      return [...prev, updated];
    });
  }, []);

  const handlePublicLocationUpdate = useCallback((data) => {
    setPublicBoaters(prev => {
      // Update or add public boater (use lat/lon as pseudo-id since anonymous)
      const index = prev.findIndex(b => 
        Math.abs(b.lat - data.lat) < 0.0001 && Math.abs(b.lon - data.lon) < 0.0001
      );
      const updated = {
        lat: data.lat,
        lon: data.lon,
        speed: data.speed,
        heading: data.heading,
        updated_at: data.updated_at,
      };
      if (index >= 0) {
        const newList = [...prev];
        newList[index] = updated;
        return newList;
      }
      return [...prev, updated];
    });
  }, []);

  const handleReportUpdate = useCallback((data) => {
    setNearbyReports(prev => {
      const index = prev.findIndex(r => r.id === data.id);
      if (index >= 0) {
        const newList = [...prev];
        newList[index] = { ...newList[index], ...data };
        return newList;
      }
      return [...prev, data];
    });
  }, []);

  const handleAlertReceived = useCallback((data) => {
    setAlerts(prev => [data, ...prev]);
    setUnreadAlerts(prev => prev + 1);
  }, []);

  const handleFriendRequestReceived = useCallback((data) => {
    setFriendRequests(prev => [data, ...prev]);
  }, []);

  // Initialize real-time updates
  const { isConnected: sseConnected, connectionStatus, updateSubscription } = useRealtimeUpdates({
    isAuthenticated,
    onFriendLocation: handleFriendLocationUpdate,
    onPublicLocation: handlePublicLocationUpdate,
    onReport: handleReportUpdate,
    onAlert: handleAlertReceived,
    onFriendRequest: handleFriendRequestReceived,
    onConnected: () => setRealtimeConnected(true),
    onDisconnected: () => setRealtimeConnected(false),
  });

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('boat360_token');
    if (token) {
      fetchUser();
    } else {
      setAuthLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('boat360_token');
      localStorage.removeItem('boat360_user');
    } finally {
      setAuthLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('[Login] Starting login for:', email);
      const response = await authAPI.login({ email, password });
      console.log('[Login] API response received');
      
      if (!response.data?.access_token) {
        throw new Error('No access token in response');
      }
      
      localStorage.setItem('boat360_token', response.data.access_token);
      console.log('[Login] Token stored in localStorage');
      
      await fetchUser();
      console.log('[Login] User fetched successfully');
      
      return response.data;
    } catch (error) {
      console.error('[Login] Error:', error);
      console.error('[Login] Error message:', error.message);
      console.error('[Login] Error response:', error.response?.data);
      throw error;
    }
  };

  const register = async (email, password, displayName, username, referralCode = '') => {
    const response = await authAPI.register({
      email,
      password,
      display_name: displayName,
      username,
      referral_code: referralCode || undefined,
    });
    localStorage.setItem('boat360_token', response.data.access_token);
    await fetchUser();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('boat360_token');
    localStorage.removeItem('boat360_user');
    setUser(null);
    setIsAuthenticated(false);
    stopLocationTracking();
  };

  const updateUser = async (data) => {
    const response = await authAPI.updateMe(data);
    setUser(response.data);
    return response.data;
  };

  // Location tracking - supports both native (Capacitor) and web
  const startLocationTracking = useCallback(async () => {
    // Check if native or web geolocation
    if (isNative) {
      // Use Capacitor location service
      const permission = await LocationService.checkPermissions();
      if (permission.location !== 'granted') {
        const newPermission = await LocationService.requestPermissions();
        if (newPermission.location !== 'granted') {
          toast.error('Location permission required');
          return;
        }
      }
    } else if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    } else {
      // Web browser - explicitly request permission first
      try {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            (error) => {
              if (error.code === 1) { // PERMISSION_DENIED
                toast.error('Location permission denied. Please enable in browser settings.');
              } else {
                toast.error('Unable to get location');
              }
              reject(error);
            },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        });
      } catch (error) {
        console.error('Location permission error:', error);
        return;
      }
    }

    if (locationWatchId.current) return;

    const handlePosition = async (position) => {
      const coords = position.coords;
      const speedKnots = (coords.speed || 0) * 1.94384; // m/s to knots

      const newLocation = {
        lat: coords.latitude,
        lon: coords.longitude,
        speed: speedKnots,
        heading: coords.heading || 0,
        accuracy: coords.accuracy || 0,
      };

      // Only update state if location actually changed significantly
      setMyLocation(prev => {
        if (!prev) return newLocation;
        // Only update if moved more than ~10 meters or speed changed significantly
        const latDiff = Math.abs(prev.lat - newLocation.lat);
        const lonDiff = Math.abs(prev.lon - newLocation.lon);
        const speedDiff = Math.abs(prev.speed - newLocation.speed);
        if (latDiff > 0.0001 || lonDiff > 0.0001 || speedDiff > 0.5) {
          return newLocation;
        }
        return prev; // Return same reference to prevent re-render
      });

      // Adaptive update frequency
      const now = Date.now();
      let updateInterval = 120000; // 2 min default (anchored)
      if (speedKnots > 5) {
        updateInterval = 10000; // 10s underway
      } else if (speedKnots > 0.5) {
        updateInterval = 30000; // 30s slow
      }

      if (!lastLocationUpdate.current || now - lastLocationUpdate.current > updateInterval) {
        try {
          // Include battery info if available (Crew Safety feature)
          const locationData = {
            ...newLocation,
            battery_level: batteryInfo.level,
            is_charging: batteryInfo.isCharging,
            app_state: document.visibilityState === 'visible' ? 'foreground' : 'background'
          };
          await locationAPI.update(locationData);
          lastLocationUpdate.current = now;
        } catch (error) {
          console.error('Failed to update location:', error);
        }
      }
    };

    const handleError = (error) => {
      console.error('Geolocation error:', error);
      toast.error('Unable to get your location');
    };

    const watchOptions = {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 5000,
    };

    if (isNative) {
      // Use Capacitor location watch
      locationWatchId.current = LocationService.watchPosition(handlePosition, watchOptions);
    } else {
      // Use browser geolocation
      locationWatchId.current = navigator.geolocation.watchPosition(
        handlePosition,
        handleError,
        watchOptions
      );
    }
  }, [batteryInfo]);

  const stopLocationTracking = useCallback(async () => {
    if (locationWatchId.current) {
      if (isNative) {
        await LocationService.clearWatch(locationWatchId.current);
      } else {
        navigator.geolocation.clearWatch(locationWatchId.current);
      }
      locationWatchId.current = null;
    }
  }, []);

  // Map data fetching
  const fetchMapData = useCallback(async () => {
    if (!isAuthenticated) return;

    // Crew and public boaters do NOT depend on our own GPS - always load them
    // so a boater can see their crew even when their own location is unavailable.
    const location = myLocationRef.current;

    try {
      const [publicRes, friendsRes] = await Promise.all([
        locationAPI.getPublicBoaters(),
        locationAPI.getFriendsLocations(),
      ]);
      setPublicBoaters(publicRes.data || []);
      setFriendsLocations(friendsRes.data || []);

      // Nearby hazard reports require our position to know where to look
      if (location) {
        const reportsRes = await reportsAPI.getNearby(location.lat, location.lon, 20);
        setNearbyReports(reportsRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch map data:', error);
    }
  }, [isAuthenticated]);

  // Friends data
  const fetchFriends = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const [friendsRes, requestsRes] = await Promise.all([
        friendsAPI.getList(),
        friendsAPI.getRequests(),
      ]);
      setFriends(friendsRes.data || []);
      setFriendRequests(requestsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    }
  }, [isAuthenticated]);

  const sendFriendRequest = async (username) => {
    await friendsAPI.sendRequest({ to_username: username });
    toast.success('Friend request sent!');
  };

  const respondToFriendRequest = async (requestId, action) => {
    await friendsAPI.respondToRequest({ request_id: requestId, action });
    await fetchFriends();
    toast.success(action === 'ACCEPTED' ? 'Friend added!' : 'Request declined');
  };

  const removeFriend = async (friendId) => {
    await friendsAPI.remove(friendId);
    await fetchFriends();
    toast.success('Friend removed');
  };

  // Trips
  const fetchActiveTrip = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await tripsAPI.getActive();
      setActiveTrip(response.data);
    } catch (error) {
      console.error('Failed to fetch active trip:', error);
    }
  }, [isAuthenticated]);

  const startTrip = async (tripData = {}) => {
    // tripData can include: destination, departure_at, expected_return_at
    const response = await tripsAPI.start({
      destination: tripData.destination || null,
      departure_at: tripData.departure_at || null,
      expected_return_at: tripData.expected_return_at || null,
    });
    setActiveTrip(response.data);
    toast.success('Trip started!');
    return response.data;
  };

  const endTrip = async () => {
    await tripsAPI.end();
    setActiveTrip(null);
    toast.success('Trip ended!');
  };

  const fetchTripHistory = async () => {
    const response = await tripsAPI.getHistory();
    setTripHistory(response.data || []);
  };

  // Reports
  const createReport = async (type, subtype, lat, lon, notes = null) => {
    const response = await reportsAPI.create({ type, subtype, lat, lon, notes });
    toast.success('Report created!');
    await fetchMapData();
    return response.data;
  };

  const voteOnReport = async (reportId, vote) => {
    await reportsAPI.vote({ report_id: reportId, vote });
    toast.success(vote === 'CONFIRM' ? 'Report confirmed' : 'Report dismissed');
    await fetchMapData();
  };

  // Alerts
  const fetchAlerts = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await alertsAPI.getReceived(true); // Only fetch unread
      setAlerts(response.data || []);
      setUnreadAlerts((response.data || []).length);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, [isAuthenticated]);

  const sendAlert = async (alertType, message = null) => {
    await alertsAPI.send({
      alert_type: alertType,
      message,
      lat: myLocation?.lat,
      lon: myLocation?.lon,
    });
    toast.success(alertType === 'SOS' ? 'SOS sent to your crew!' : 'Status shared with crew');
  };

  // Subscription (Boat 360 Freemium)
  const restoreAttempted = useRef(false);

  // Store product ids (must match App Store Connect / Play Console)
  const IOS_PRODUCT_ID = 'com.safetymindai.boat360.plus.annual';
  const ANDROID_PRODUCT_ID = 'boat360_plus_yearly';

  const activatePlus = useCallback(async (pid) => {
    await subscriptionAPI.verify({ platform, product_id: pid, purchase_token: `auto-restore-${Date.now()}` });
    const refreshed = await subscriptionAPI.getStatus();
    setSubscription(refreshed.data);
    if (refreshed.data?.is_paid) toast.success('🎉 Boat 360 Plus activated!');
  }, []);

  // Silently detect an active subscription (e.g. redeemed offer code, renewal,
  // or a purchase made before this account existed) and sync it to the backend.
  // iOS: StoreKit 2 currentEntitlements via local plugin (NO password prompt).
  // Android: capgo restorePurchases (works, returns customerInfo).
  const autoRestoreEntitlement = useCallback(async () => {
    if (!isNative) return;
    try {
      if (platform === 'ios') {
        const sk = registerPlugin('StoreKitLocal');
        const res = await sk.getActiveEntitlements();
        const ents = res?.entitlements || [];
        if (ents.some((e) => e.productId === IOS_PRODUCT_ID)) await activatePlus(IOS_PRODUCT_ID);
      } else {
        const { NativePurchases } = await import('@capgo/native-purchases');
        const result = await NativePurchases.restorePurchases();
        const info = result?.customerInfo || {};
        const active = info.activeSubscriptions || [];
        const owned = info.allPurchasedProductIdentifiers || [];
        if (active.includes(ANDROID_PRODUCT_ID) || owned.includes(ANDROID_PRODUCT_ID)) {
          await activatePlus(ANDROID_PRODUCT_ID);
        }
      }
    } catch (e) {
      console.log('[IAP] auto-restore skipped:', e?.message);
    }
  }, [activatePlus]);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await subscriptionAPI.getStatus();
      setSubscription(response.data);
      // First time we learn the user is NOT paid, try a silent restore in case
      // they redeemed a code or bought on this store account previously.
      if (isNative && !response.data?.is_paid && !restoreAttempted.current) {
        restoreAttempted.current = true;
        autoRestoreEntitlement();
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  }, [isAuthenticated, autoRestoreEntitlement]);

  // Allow the silent restore to run again if the user switches accounts
  useEffect(() => {
    if (!isAuthenticated) restoreAttempted.current = false;
  }, [isAuthenticated]);

  // iOS: listen for StoreKit transactions (offer-code redemptions & renewals)
  // delivered outside the purchase button, and activate Plus automatically.
  useEffect(() => {
    if (!isNative || platform !== 'ios' || !isAuthenticated) return;
    let handle;
    try {
      const sk = registerPlugin('StoreKitLocal');
      Promise.resolve(
        sk.addListener('onTransactionUpdate', (data) => {
          if (data?.productId === IOS_PRODUCT_ID) activatePlus(IOS_PRODUCT_ID);
        })
      ).then((h) => { handle = h; }).catch(() => {});
    } catch (e) { /* plugin not present */ }
    return () => { try { handle?.remove?.(); } catch (e) {} };
  }, [isAuthenticated, activatePlus]);

  const upgradeSubscription = async () => {
    try {
      const response = await subscriptionAPI.upgrade();
      setSubscription({ ...subscription, tier: response.data.tier, is_paid: response.data.tier === 'PAID' });
      toast.success('Welcome to Boat 360 Plus!');
      // Refresh subscription to get full features
      await fetchSubscription();
      return response.data;
    } catch (error) {
      toast.error('Upgrade failed. Please try again.');
      throw error;
    }
  };

  // SOS System (Boat 360 Freemium)
  const fetchActiveSOS = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await sosAPI.getActive();
      setActiveSOS(response.data);
    } catch (error) {
      console.error('Failed to fetch active SOS:', error);
    }
  }, [isAuthenticated]);

  const fetchNearbyPublicSOS = useCallback(async () => {
    if (!isAuthenticated || !myLocation) return;
    try {
      const response = await sosAPI.getNearby(myLocation.lat, myLocation.lon);
      setNearbyPublicSOS(response.data || []);
    } catch (error) {
      console.error('Failed to fetch nearby SOS:', error);
    }
  }, [isAuthenticated, myLocation]);

  const fetchFriendsSOS = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await sosAPI.getFriendsSOS();
      setFriendsSOS(response.data || []);
    } catch (error) {
      console.error('Failed to fetch friends SOS:', error);
    }
  }, [isAuthenticated]);

  const triggerSOS = async (distressType, sosType = 'PUBLIC') => {
    if (!myLocation) {
      toast.error('Location required to send SOS');
      return;
    }
    try {
      const response = await sosAPI.trigger({
        sos_type: sosType,
        distress_type: distressType,
        lat: myLocation.lat,
        lon: myLocation.lon,
      });
      toast.success(response.data.message);
      await fetchActiveSOS();
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send SOS';
      toast.error(detail);
      throw error;
    }
  };

  const cancelSOS = async (sosId, sosType) => {
    try {
      await sosAPI.cancel(sosId, sosType);
      toast.success('SOS cancelled');
      await fetchActiveSOS();
    } catch (error) {
      toast.error('Failed to cancel SOS');
      throw error;
    }
  };

  const acknowledgeSOS = async (sosId, sosType) => {
    try {
      const response = await sosAPI.acknowledge({ sos_id: sosId, sos_type: sosType });
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge SOS');
      throw error;
    }
  };

  // Emergency Markers (MOB - Man Overboard)
  const fetchEmergencyMarkers = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await emergencyMarkerAPI.getAll();
      setEmergencyMarkers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch emergency markers:', error);
    }
  }, [isAuthenticated]);

  const createEmergencyMarker = async (markerData) => {
    if (!myLocation) {
      toast.error('Location required to create marker');
      return null;
    }
    try {
      const response = await emergencyMarkerAPI.create({
        lat: markerData.lat || myLocation.lat,
        lon: markerData.lon || myLocation.lon,
        marker_type: markerData.marker_type || 'PERSON_OVERBOARD',
        label: markerData.label || null,
        broadcast_to_crew: markerData.broadcast_to_crew !== false  // Default true
      });
      toast.success('Emergency marker created');
      await fetchEmergencyMarkers();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create marker');
      throw error;
    }
  };

  const clearEmergencyMarker = async (markerId) => {
    try {
      await emergencyMarkerAPI.clear(markerId);
      toast.success('Marker cleared');
      await fetchEmergencyMarkers();
    } catch (error) {
      toast.error('Failed to clear marker');
      throw error;
    }
  };

  // Battery polling (Crew Safety feature)
  useEffect(() => {
    if (!isAuthenticated) return;

    const pollBattery = async () => {
      try {
        if (isNative && BatteryService) {
          const info = await BatteryService.getBatteryInfo();
          setBatteryInfo({
            level: info.batteryLevel,
            isCharging: info.isCharging
          });
        } else if (navigator.getBattery) {
          // Web fallback - Battery Status API (limited support)
          const battery = await navigator.getBattery();
          setBatteryInfo({
            level: battery.level,
            isCharging: battery.charging
          });
        }
      } catch (error) {
        console.log('Battery info not available:', error.message);
      }
    };

    // Initial poll
    pollBattery();
    // Poll every 60 seconds
    const batteryInterval = setInterval(pollBattery, 60000);

    return () => clearInterval(batteryInterval);
  }, [isAuthenticated]);

  // Persistent "Always share my location" background tracking (independent of trips)
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const wantsPersistent = !!user.always_share_location;

    if (wantsPersistent && hasBackgroundLocationConsent()) {
      BackgroundLocationService.startPersistentTracking({
        onLocation: async (loc) => {
          try {
            await locationAPI.update({
              lat: loc.lat,
              lon: loc.lon,
              speed: loc.speed || 0,
              heading: loc.heading || 0,
              accuracy: loc.accuracy || 0,
              source: 'bg',
              battery_level: batteryInfoRef.current.level,
              is_charging: batteryInfoRef.current.isCharging,
              app_state: 'background',
            });
          } catch (error) {
            console.error('Failed to send persistent location update:', error);
          }
        },
      });
    } else {
      BackgroundLocationService.stopPersistentTracking();
    }
  }, [isAuthenticated, user]);

  // Auto-fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      startLocationTracking();
      fetchFriends();
      fetchMapData();
      fetchActiveTrip();
      fetchAlerts();
      fetchSubscription();
      fetchActiveSOS();
      fetchEmergencyMarkers();

      // Set up polling intervals - slower when SSE is connected
      const mapPollInterval = realtimeConnected ? 30000 : 15000; // 30s with SSE, 15s fallback
      const alertPollInterval = realtimeConnected ? 60000 : 30000; // 60s with SSE, 30s fallback
      const sosPollInterval = 30000; // 30s for SOS updates
      const markerPollInterval = 30000; // 30s for emergency markers
      
      const mapInterval = setInterval(fetchMapData, mapPollInterval);
      const alertInterval = setInterval(fetchAlerts, alertPollInterval);
      const sosInterval = setInterval(() => {
        fetchActiveSOS();
        fetchNearbyPublicSOS();
        fetchFriendsSOS();
      }, sosPollInterval);
      const markerInterval = setInterval(fetchEmergencyMarkers, markerPollInterval);

      return () => {
        clearInterval(mapInterval);
        clearInterval(alertInterval);
        clearInterval(sosInterval);
        clearInterval(markerInterval);
      };
    }
  }, [isAuthenticated, realtimeConnected, startLocationTracking, fetchFriends, fetchActiveTrip, fetchAlerts, fetchMapData, fetchSubscription, fetchActiveSOS, fetchNearbyPublicSOS, fetchFriendsSOS, fetchEmergencyMarkers]);

  const value = {
    // Auth
    user,
    isAuthenticated,
    authLoading,
    login,
    register,
    logout,
    updateUser,

    // Location
    myLocation,
    mapCenter,
    setMapCenter,
    focusLocation,
    setFocusLocation,
    startLocationTracking,
    stopLocationTracking,

    // Map data
    publicBoaters,
    friendsLocations,
    nearbyReports,
    fetchMapData,

    // Friends
    friends,
    friendRequests,
    fetchFriends,
    sendFriendRequest,
    respondToFriendRequest,
    removeFriend,

    // Trips
    activeTrip,
    tripHistory,
    startTrip,
    endTrip,
    fetchTripHistory,

    // Reports
    createReport,
    voteOnReport,

    // Alerts
    alerts,
    unreadAlerts,
    sendAlert,
    fetchAlerts,

    // Subscription (Boat 360 Freemium)
    subscription,
    isPaid: subscription?.is_paid || false,
    fetchSubscription,
    upgradeSubscription,

    // SOS System (Boat 360 Freemium)
    activeSOS,
    nearbyPublicSOS,
    friendsSOS,
    triggerSOS,
    cancelSOS,
    acknowledgeSOS,
    fetchActiveSOS,
    fetchNearbyPublicSOS,
    fetchFriendsSOS,

    // Emergency Markers (MOB)
    emergencyMarkers,
    createEmergencyMarker,
    clearEmergencyMarker,
    fetchEmergencyMarkers,

    // Battery Info (Crew Safety)
    batteryInfo,

    // Real-time
    realtimeConnected,
    connectionStatus,
    updateSubscription,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
